import React, { useEffect, useState, useRef } from 'react';
import { 
  BellIcon, 
  ArchiveBoxIcon, 
  CheckCircleIcon, 
  ArrowTopRightOnSquareIcon, 
  XMarkIcon 
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../store/slices/authSlice';
import { inventoryService } from '../../services/inventoryService';

interface NotificationBadgeProps {
  roles: string[];
  permissions: string[];
}

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({ roles, permissions }) => {
  const [requests, setRequests] = useState<any[]>([]);
  const [openedIds, setOpenedIds] = useState<number[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const storageKey = `opened_notification_ids_${user?.id || 'default'}`;

  const hasAccess = roles.includes('superadmin') || roles.includes('admin') || permissions.some(p => p.startsWith('inventori'));

  // Muat daftar ID notifikasi yang sudah pernah dibuka dari localStorage
  const loadOpenedIds = (): number[] => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  };

  useEffect(() => {
    setOpenedIds(loadOpenedIds());

    const handleSync = () => {
      setOpenedIds(loadOpenedIds());
    };

    window.addEventListener('notifications-read', handleSync);
    window.addEventListener('storage', handleSync);
    return () => {
      window.removeEventListener('notifications-read', handleSync);
      window.removeEventListener('storage', handleSync);
    };
  }, [storageKey]);

  // Tutup dropdown jika klik di luar area komponen
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Polling data permintaan order barang berstatus pending
  useEffect(() => {
    if (!hasAccess) return;
    
    let isMounted = true;
    const fetchPendingRequests = async () => {
      try {
        const res = await inventoryService.getInventoryRequests({ status: 'pending' });
        if (isMounted && res.data) {
          setRequests(res.data);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    fetchPendingRequests();
    
    // Refresh otomatis setiap 2 menit
    const intervalId = setInterval(fetchPendingRequests, 120000);
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [hasAccess]);

  // Hitung berapa notifikasi pending yang BELUM dibuka oleh user
  const unopenedCount = requests.filter(req => !openedIds.includes(req.id)).length;

  // Tandai semua notifikasi saat ini sebagai telah dibuka
  const markAllAsOpened = () => {
    if (requests.length === 0) return;
    const currentIds = requests.map(r => r.id);
    const updated = Array.from(new Set([...openedIds, ...currentIds]));
    setOpenedIds(updated);
    try {
      localStorage.setItem(storageKey, JSON.stringify(updated));
      window.dispatchEvent(new Event('notifications-read'));
    } catch (e) {
      console.error('Gagal menyimpan status notifikasi dibaca', e);
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      // Saat membuka dropdown notifikasi, langsung tandai semua notifikasi saat ini sebagai sudah dibuka
      markAllAsOpened();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleGoToApprovals = () => {
    markAllAsOpened();
    setIsOpen(false);
    navigate('/inventory/order-approval');
  };

  const formatTimeAgo = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return 'Baru saja';
      if (diffMins < 60) return `${diffMins} menit lalu`;
      if (diffHours < 24) return `${diffHours} jam lalu`;
      if (diffDays < 7) return `${diffDays} hari lalu`;
      return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button 
        type="button"
        onClick={handleToggle}
        className="bg-white p-1.5 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-150 relative"
        title={unopenedCount > 0 ? `${unopenedCount} Notifikasi baru belum dibuka` : "Notifikasi"}
        aria-label="Notifikasi"
      >
        <BellIcon className="h-6 w-6 text-gray-600" />
        {/* Warna merah HANYA muncul jika ada notifikasi baru yang belum dibuka */}
        {unopenedCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[11px] font-bold leading-none text-white bg-red-600 rounded-full transform translate-x-1/4 -translate-y-1/4 shadow-sm animate-pulse">
            {unopenedCount > 99 ? '99+' : unopenedCount}
          </span>
        )}
      </button>

      {/* Panel Dropdown Notifikasi */}
      {isOpen && (
        <div className="origin-top-right absolute right-0 mt-2 w-80 sm:w-96 rounded-2xl shadow-2xl bg-white ring-1 ring-black/5 border border-gray-100 z-50 overflow-hidden animate-fade-in-up">
          {/* Header */}
          <div className="p-3.5 px-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-800 text-sm">Notifikasi</span>
              {requests.length > 0 && (
                <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-primary-50 text-primary-700 border border-primary-200">
                  {requests.length} Permintaan
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
              title="Tutup"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Daftar Notifikasi */}
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
            {requests.length === 0 ? (
              <div className="py-8 px-4 text-center">
                <div className="w-10 h-10 mx-auto rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 mb-2">
                  <CheckCircleIcon className="w-6 h-6" />
                </div>
                <p className="text-xs font-semibold text-gray-700">Tidak ada notifikasi baru</p>
                <p className="text-[11px] text-gray-500 mt-0.5">Semua permintaan order barang telah diproses</p>
              </div>
            ) : (
              requests.map((req) => (
                <div
                  key={req.id}
                  onClick={handleGoToApprovals}
                  className="p-3.5 hover:bg-gray-50/80 cursor-pointer transition-colors duration-150 flex items-start gap-3 text-left group"
                >
                  <div className="p-2 rounded-xl bg-amber-50 text-amber-600 group-hover:bg-amber-100 transition-colors mt-0.5">
                    <ArchiveBoxIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-gray-900 truncate">
                        Permintaan Order #{req.id}
                      </p>
                      <span className="text-[10px] text-gray-400 whitespace-nowrap">
                        {formatTimeAgo(req.createdAt)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5 truncate">
                      {req.employee?.name ? `Dari: ${req.employee.name}` : (req.notes || 'Order barang baru')}
                    </p>
                    {req.items && req.items.length > 0 && (
                      <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                        {req.items.length} jenis item
                      </p>
                    )}
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800">
                        Menunggu Persetujuan
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {hasAccess && (
            <div className="p-2.5 bg-gray-50/80 border-t border-gray-100 text-center">
              <button
                type="button"
                onClick={handleGoToApprovals}
                className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors duration-150 inline-flex items-center justify-center gap-1.5 w-full py-1"
              >
                <span>Buka Persetujuan Order Barang</span>
                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
